import React from "react";
import PropTypes from "prop-types";
import { Button, Typography, Grid } from "@mui/material";
import { Formik, Field } from "formik";
import { TextField } from "formik-mui";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import DatePickerField from "../../components/FormikFields/DatePickerField";
import { formatDate, isValidDate } from "../../utils/datetime";

const getSchema = t => {
  const maxNameLength = 150;

  return Yup.object().shape({
    name: Yup.string()
      .required(t("validationMessages.nameRequired"))
      .max(
        maxNameLength,
        t("validationMessages.cannotExceedLength", {
          type: "Name",
          maxLength: maxNameLength
        })
      ),
    dueDate: Yup.mixed().test(
      "dueDate",
      t("validationMessages.validDate"),
      function() {
        return isValidDate(this.parent.dueDate);
      }
    )
  });
};

function AddNewSpecialAssignmentModal(props) {
  const { open, onClose, onCreateAssignment, courseDetails, disabled } = props;
  const { t } = useTranslation();
  const initialValues = {
    id: courseDetails ? courseDetails.id : "",
    name: "",
    dueDate: courseDetails
      ? new Date(Date.now() + courseDetails.daysToComplete * 24 * 3600 * 1000)
      : new Date()
  };

  return (
    courseDetails && (
      <Formik
        initialValues={initialValues}
        validationSchema={getSchema(t)}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          if (values.dueDate) {
            values.dueDate = formatDate(values.dueDate, "YYYY-MM-DD");
          }
          onCreateAssignment(values);
        }}
      >
        {({ submitForm, resetForm }) => (
          <Modal
            scrollable
            fullWidth
            dialogTitle={
              <Typography variant="h6" color="textPrimary">
                {t("specialAssignments.configureAssignment", {
                  name: courseDetails ? courseDetails.name : ""
                })}
              </Typography>
            }
            dialogContent={
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" color="textPrimary">
                    {t("specialAssignments.duration")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("table.trainingHoursColumn.fullTime", {
                      minutes: courseDetails.durationMinutes,
                      hours: courseDetails.durationHours
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" color="textPrimary">
                    {t("specialAssignments.passingScore")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {courseDetails.passingScore}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" color="textPrimary">
                    {t("specialAssignments.name")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    variant="filled"
                    fullWidth
                    component={TextField}
                    name="name"
                    inputProps={{
                      "data-qa": "add-new-group-modal-name-input"
                    }}
                    disabled={disabled}
                    hiddenLabel
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" color="textPrimary">
                    {t("specialAssignments.dueDate")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    minDate={new Date()}
                    name="dueDate"
                    component={DatePickerField}
                    inputDataQa="add-new-group-modal-due-date-input"
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            }
            dialogActions={
              <>
                <Button
                  color="primary"
                  disabled={disabled}
                  onClick={() => {
                    onClose();
                    resetForm();
                  }}
                >
                  {t("cancelBtn")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submitForm}
                  disabled={disabled}
                >
                  {t("addAssignment")}
                </Button>
              </>
            }
            open={open}
            onClose={() => {
              onClose();
              resetForm();
            }}
            showCloseIcon
          />
        )}
      </Formik>
    )
  );
}

AddNewSpecialAssignmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreateAssignment: PropTypes.func,
  courseDetails: PropTypes.any,
  disabled: PropTypes.bool
};

export default AddNewSpecialAssignmentModal;
